<script>
import { editorState } from "@app/store/editorStore.svelte.js";
import {
    commitHistory
} from '@app/store/historyStore.svelte.js';
import {
    showModal
} from '@app/store/modalStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    eventGroupState, isSelectedEventGroupLocked
} from '@app/store/eventGroupStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import fixOverlap from '@cclib/functions/eventGroups/fixOverlap.js';
import sortByTime from '@cclib/functions/eventGroups/orderByTime.js';
import convertToPlainText from '@cclib/functions/quill/convertToPlainText.js';
import convertToPlainTextCustom from '@cclib/functions/quill/convertToPlainTextCustom.js';
import convertToHtml from '@cclib/functions/quill/convertToHtml.js';
import convertToPopOn from '@cclib/functions/special/convertToPopOn.js';
import fixRtlText from '@cclib/functions/utility/fixRtlText.js'; 
import reverseRtlStartEnd from '@cclib/functions/utility/reverseRtlStartEnd.js'; 
import quillClasses from '@cclib/dict/quillClasses.js';
import { current } from 'immer';
import * as m from "@app/paraglide/messages.js";
import Menu from "../menu/Menu.svelte";
import MenuItem from "../menu/MenuItem.svelte";
import MenuDivider from "../menu/MenuDivider.svelte";
import SubMenu from "../menu/SubMenu.svelte";

let isLocked = $derived(isSelectedEventGroupLocked());
let hasSelection = $derived(
    Boolean(eventGroupState[projectState.selected]?.selected?.length > 0),
);

function guardedShowModal(modalName) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }
    showModal(modalName);
}

function spellNumbers() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        let numbers = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

        commitHistory({
            name: "spell numbers",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    draft[egIdx].events.forEach((event, index, events) => {
                        events[index].text = event.text.replace(/\b\d\b/g, m => numbers[m]);
                    });
                    draft[egIdx].selected = [];
                },
            },
        });
        
        toast.push(m.spelling_numbers_complete(), {classes: ['toast-success']});
    } catch (err) {
        console.log(err);
        toast.push(m.spelling_numbers_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
    }
}

function removeRepeatWords() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;

        commitHistory({
            name: "remove repeat words",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    draft[egIdx].events.forEach((event, index, events) => {
                        try {
                            let plainText = convertToPlainText(event.text, " ");
                            let matches = plainText.match(/(\b\S+\b)(?=\s\1)/gmi);
                            if (matches){
                                plainText = plainText.replace(/(\b\S+\b)(?=\s\1)/gmi, "");
                                events[index].text = convertToHtml(plainText);
                            }      
                        } catch(e){
                            console.log(e);
                            console.log(index);
                            console.log(e.message);
                        }
                    });
                    draft[egIdx].selected = [];
                },
            },
        });

        toast.push(m.removing_repeat_words_complete(), {classes: ['toast-success']});
    } catch (err) {
        console.log(err);
        toast.push(m.removing_repeat_words_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
    }
}

function replaceWithEllipses() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;

        commitHistory({
            name: "ellipses replacement",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    draft[egIdx].events.forEach((event, index, events) => {
                        events[index].text = event.text.replace(/\.\.+/gm, "…");
                    });
                    draft[egIdx].selected = [];
                },
            },
        });

        toast.push(m.ellipses_replacement_complete(), {classes: ['toast-success']});
    } catch (err) {
        console.log(err);
        toast.push(m.ellipses_replacement_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
    }
}

function fixEventOverlap(eventGroup) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    const frameGap = editorState.minFrameGap;
    const frameRate = projectState.frameRate;

    commitHistory({
        name: "fix overlap",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                draft[eventGroup] = fixOverlap(
                    current(draft[eventGroup]),
                    frameGap,
                    frameRate,
                );
            },
        },
    });

    toast.push(m.fixed_event_overlap_success(), {classes: ['toast-success']});

}

function orderByTime(eventGroup) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;

    commitHistory({
        name: "ordering",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                draft[eventGroup] = sortByTime(current(draft[eventGroup]));
                draft[egIdx].selected = [];
            },
        },
    });

    toast.push(m.events_ordered_by_time(), {classes: ['toast-success']});
}

function snapToEventEnds() {
    /* 0.5 seconds is the range we're looking for but it could be tighter... not sure yet. */
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        const frameGap = editorState.minFrameGap;
        const frameRate = projectState.frameRate;

        commitHistory({
            name: "snap to events",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    const sorted = sortByTime(current(draft[egIdx]));
                    sorted.events.forEach((event, eventIndex, eventsArray) => {
                        if (eventIndex > 0 && event.start <= eventsArray[eventIndex - 1].end + 0.5) {
                            eventsArray[eventIndex].start = eventsArray[eventIndex - 1].end + (frameGap / frameRate);
                        }
                    });
                    draft[egIdx] = sorted;
                },
            },
        });

        toast.push(m.snap_to_event_ends_success(), {classes: ['toast-success']});

    } catch (err) {
        console.log(err, err.message);
        toast.push(m.snap_to_event_ends_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
    }

}

function snapToEventStarts() {
    /* 0.5 seconds is the range we're looking for but it could be tighter... not sure yet. */
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        const frameGap = editorState.minFrameGap;
        const frameRate = projectState.frameRate;

        commitHistory({
            name: "snap to events",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    const sorted = sortByTime(current(draft[egIdx]));
                    sorted.events.forEach((event, eventIndex, eventsArray) => {
                        if (eventsArray[eventIndex + 1] && event.end >= eventsArray[eventIndex + 1].start - 0.5) {
                            eventsArray[eventIndex].end = eventsArray[eventIndex + 1].start - (frameGap / frameRate);
                        }
                    });
                    draft[egIdx] = sorted;
                },
            },
        });

        toast.push(m.snap_to_event_start_success(), {classes: ['toast-success']});

    } catch (err) {
        console.log(err, err.message);
        toast.push(m.snap_to_event_start_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
    }

}

function unwrapElement(element) {
    element.replaceWith(...element.childNodes);
}

function removeInlineFormatting(text) {
    if (!text) {
        return text;
    }

    const template = document.createElement("template");
    template.innerHTML = text;
    const colorFormattedSpans = [];

    template.content.querySelectorAll("em, u, strong").forEach(unwrapElement);
    template.content.querySelectorAll("[style]").forEach(element => {
        const hadColorFormatting = element.style.getPropertyValue("color") || element.style.getPropertyValue("background-color");

        element.style.removeProperty("color");
        element.style.removeProperty("background-color");

        if (!element.getAttribute("style")?.trim()) {
            element.removeAttribute("style");
        }

        if (hadColorFormatting && element.tagName.toLowerCase() === "span") {
            colorFormattedSpans.push(element);
        }
    });

    colorFormattedSpans.forEach(element => {
        if (element.parentNode && element.attributes.length === 0) {
            unwrapElement(element);
        }
    });

    return template.innerHTML;
}

function clearFormatting() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        const selected = [...eventGroupState[egIdx].selected];

        commitHistory({
            name: "clear formatting",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    selected.forEach((eventIndex) => {
                        draft[egIdx].events[eventIndex].text = removeInlineFormatting(
                            draft[egIdx].events[eventIndex].text,
                        );
                    });
                    draft[egIdx].selected = [];
                },
            },
        });

        toast.push(m.clear_formatting_success(), {classes: ['toast-success']});
    } catch (err) {
        console.log(err, err.message);
        toast.push(err.message, {classes: ['toast-danger']});
    } finally {
    }
}

function boldEvent() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        const selected = [...eventGroupState[egIdx].selected];

        commitHistory({
            name: "bold event(s)",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    selected.forEach((eventIndex) => {
                        if (draft[egIdx].events[eventIndex].text.match(/<strong>/)) {
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/<strong>|<\/strong>/gmi, "");
                        } else {
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/(?<=>)\s/g, "");
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/(?<=>)(?!<|$)/gmi, "<strong>").replace(/(?=<\/p)/gmi, "</strong>");
                        }
                    });
                    draft[egIdx].selected = [];
                },
            },
        });

        toast.push(m.bold_event_text(), {classes: ['toast-success']});
    } catch (err) {
        console.log(err, err.message);
        toast.push(err.message, {classes: ['toast-danger']});
    } finally {
    }
}

function underlineEvent() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        const selected = [...eventGroupState[egIdx].selected];

        commitHistory({
            name: "underline event(s)",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    selected.forEach((eventIndex) => {
                        if (draft[egIdx].events[eventIndex].text.match(/<u>/)) {
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/<u>|<\/u>/gmi, "");
                        } else {
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/(?<=>)\s/g, "");
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/(?<=>)(?!<|$)/gmi, "<u>").replace(/(?=<\/p)/gmi, "</u>");
                        }            
                    });
                    draft[egIdx].selected = [];
                },
            },
        });

        toast.push(m.underline_event_text(), {classes: ['toast-success']});
    } catch (err) {
        console.log(err, err.message);
        toast.push(err.message, {classes: ['toast-danger']});
    } finally {
    }
}

function italicizeEvent() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        const selected = [...eventGroupState[egIdx].selected];

        commitHistory({
            name: "italicize event(s)",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    selected.forEach((eventIndex) => {
                        if (draft[egIdx].events[eventIndex].text.match(/<em>/)) {
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/<em>|<\/em>/gmi, "");
                        } else {
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/(?<=>)\s/g, "");
                            draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/(?<=>)(?!<|$)/gmi, "<em>").replace(/(?=<\/p)/gmi, "</em>");
                        }            
                    });
                    draft[egIdx].selected = [];
                },
            },
        });

        toast.push(m.italicize_event_text(), {classes: ['toast-success']});
    } catch (err) {
        console.log(err, err.message);
        toast.push(err.message, {classes: ['toast-danger']});
    } finally {
    }
}

function fixRtlEventText(){
    try {
        if (eventGroupState[projectState.selected].selected.length === 0){
            throw new Error(m.no_events_selected());
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        const selected = [...eventGroupState[egIdx].selected];

        commitHistory({
            name: "Fix RTL Text",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    selected.forEach((eventIndex) => {
                        let plainText = convertToPlainText(draft[egIdx].events[eventIndex].text);
                        let correctedText = fixRtlText(plainText);
                        draft[egIdx].events[eventIndex].text = convertToHtml(correctedText);            
                    });
                    draft[egIdx].selected = [];
                },
            },
        });

        toast.push(m.rtl_text_fixed(), {classes: ['toast-success']});
    } catch (err){
        console.log(err, err.message);
        toast.push(err.message, {classes: ['toast-danger']});
    } finally {
    }
}

function reverseRtlStartAndEnd(){
    try {
        if (eventGroupState[projectState.selected].selected.length === 0){
            throw new Error(m.no_events_selected());
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;
        const selected = [...eventGroupState[egIdx].selected];

        commitHistory({
            name: "Fix RTL Text",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    selected.forEach((eventIndex) => {
                        draft[egIdx].events[eventIndex] = reverseRtlStartEnd(
                            current(draft[egIdx].events[eventIndex]),
                        );
                    });
                    draft[egIdx].selected = [];
                },
            },
        });

        toast.push(m.rtl_text_fixed(), {classes: ['toast-success']});
    } catch (err){
        console.log(err, err.message);
        toast.push(err.message, {classes: ['toast-danger']});
    } finally {
    }
} 

function convertEventsToPopOn(){
    try {
        if (!eventGroupState[projectState.selected]){
            throw new Error("No Event Group selected");
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        const egIdx = projectState.selected;

        commitHistory({
            name: "Convert to Pop-On",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    draft[egIdx] = convertToPopOn(current(draft[egIdx]));
                },
            },
        });
    } catch(err){
        console.log(err, err.message);
        toast.push(err.message, {classes: ['toast-danger']});
    } finally {
    }
}


function convertToUpperCase(){
    //convert the text property of all selected events to uppercase
    if (!eventGroupState[projectState.selected] || eventGroupState[projectState.selected].selected.length === 0){
        toast.push(m.no_events_selected_uppercase(), {classes: ['toast-warning']});
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];

    commitHistory({
        name: "convert to upper case",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.toUpperCase();        
                });
                draft[egIdx].selected = [];
            },
        },
    });

}

function convertToLowerCase(){
    if (!eventGroupState[projectState.selected] || eventGroupState[projectState.selected].selected.length === 0){
        toast.push(m.no_events_selected_lowercase(), {classes: ['toast-warning']});
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];

    commitHistory({
        name: "convert to lower case",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.toLowerCase();        
                });
                draft[egIdx].selected = [];
            },
        },
    });

}

function convertToTitleCase(){
    if (!eventGroupState[projectState.selected] || eventGroupState[projectState.selected].selected.length === 0){
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];

    commitHistory({
        name: "convert to title case",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.toLowerCase().replace(/\b\w/g, match => match.toUpperCase());        
                });
                draft[egIdx].selected = [];
            },
        },
    });

}

function convertToSentenceCase(){
    if (!eventGroupState[projectState.selected] || eventGroupState[projectState.selected].selected.length === 0){
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];

    commitHistory({
        name: "convert to title case",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    //convert the text property of all selected events to sentence case. For example `<p class="ql-align-center">play dungeons and dragons.</p>` should convert to `<p class="ql-align-center">Play dungeons and dragons.</p>`
                    draft[egIdx].events[eventIndex].text = convertToHtml(convertToPlainTextCustom(draft[egIdx].events[eventIndex].text).toLowerCase().replace(/(^\w|([.!?])\s\w)/g, match => match.toUpperCase()));    
                });
                draft[egIdx].selected = [];
            },
        },
    });

}

function splitOnDash(){
    //Split the text property of all selected events on the short and long dash characters when the dash is preceded by a space or followed by a space. For example, `<p class="ql-align-center">-Det er Dracula! -Drep ham.</p>` should become <p class="ql-align-center">-Det er Dracula!</p><p class="ql-align-center">-Drep ham.</p>
    if (!eventGroupState[projectState.selected] || eventGroupState[projectState.selected].selected.length === 0){
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];
    
    commitHistory({
        name: "split on dash",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    const alignment = draft[egIdx].events[eventIndex].alignment;
                    draft[egIdx].events[eventIndex].text = draft[egIdx].events[eventIndex].text.replace(/(\s-|-\s)/g, match => {
                        return match === " -" ? `</p><p class='${quillClasses.align[alignment]}'>-` : `-</p><p class='${quillClasses.align[alignment]}'>`;
                    });        
                });
                draft[egIdx].selected = [];
            },
        },
    });

}
</script>

<Menu id="format" label={m.format()} ariaLabel={m.format_menu()}>
    <MenuItem
        label={m.bold()}
        icon="bi-type-bold"
        id="boldButton"
        disabled={!hasSelection || isLocked}
        disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
        onSelect={boldEvent} />
    <MenuItem
        label={m.underline()}
        icon="bi-type-underline"
        id="underlineButton"
        disabled={!hasSelection || isLocked}
        disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
        onSelect={underlineEvent} />
    <MenuItem
        label={m.italics()}
        icon="bi-type-italic"
        id="italicsButton"
        disabled={!hasSelection || isLocked}
        disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
        onSelect={italicizeEvent} />
    <MenuItem
        label={m.clear_formatting()}
        icon="bi-x-lg"
        id="clearFormattingButton"
        disabled={!hasSelection || isLocked}
        disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
        onSelect={clearFormatting} />
    <MenuDivider />
    <MenuItem
        label={m.auto_format_custom()}
        icon="bi-textarea-t"
        id="autoFormatButton"
        shortcut="Auto Format"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => guardedShowModal("autoFormat")} />
    <MenuItem
        label={m.snap_to_shot_changes()}
        icon="bi-scissors"
        id="snapToShotsButton"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => guardedShowModal("snapToShotChanges")} />
    <MenuItem
        label={m.snap_to_event_start()}
        icon="bi-skip-start"
        id="snapToEventStartsButton"
        shortcut="Chain To Next Event"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={snapToEventStarts} />
    <MenuItem
        label={m.snap_to_event_end()}
        icon="bi-skip-end"
        id="snapToEventEndsButton"
        shortcut="Chain To Previous Event"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={snapToEventEnds} />
    <MenuDivider />
    <MenuItem
        label={m.fix_event_overlap()}
        icon="bi-intersect"
        id="fixEventOverlapButton"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => fixEventOverlap(projectState.selected)} />
    <MenuItem
        label={m.order_events_by_time()}
        icon="bi-sort-down"
        id="orderByTimeButton"
        shortcut="Order Events by Time"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => orderByTime(projectState.selected)} />
    <MenuItem
        label={m.convert_to_pop_on()}
        icon="bi-square"
        id="convertToPopOnButton"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={convertEventsToPopOn} />
    <MenuDivider />
    <MenuItem
        label={m.caption_style()}
        icon="bi-palette"
        id="captionStyleButton"
        disabled={!hasSelection || isLocked}
        disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
        onSelect={() => guardedShowModal("captionStyle")} />
    <MenuItem
        label={m.caption_position()}
        icon="bi-arrows-move"
        id="captionPositionButton"
        disabled={!hasSelection || isLocked}
        disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
        onSelect={() => guardedShowModal("captionPosition")} />
    <MenuDivider />
    <SubMenu label={m.text()} icon="bi-fonts">
        <MenuItem
            label={m.spell_numbers()}
            id="spellNumbersButton"
            disabled={isLocked}
            disabledReason={m.event_group_locked_toast()}
            onSelect={spellNumbers} />
        <MenuItem
            label={m.remove_repeat_words()}
            id="removeRepeatWordsButton"
            disabled={isLocked}
            disabledReason={m.event_group_locked_toast()}
            onSelect={removeRepeatWords} />
        <MenuItem
            label={m.replace_ellipses()}
            id="replaceWithEllipsesButton"
            disabled={isLocked}
            disabledReason={m.event_group_locked_toast()}
            onSelect={replaceWithEllipses} />
        <MenuItem
            label={m.split_on_dash()}
            id="splitOnDashButton"
            disabled={!hasSelection || isLocked}
            disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
            onSelect={splitOnDash} />
        <MenuDivider />
        <MenuItem
            label={m.fix_rtl_text()}
            id="fixRtlTextButton"
            disabled={!hasSelection || isLocked}
            disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
            onSelect={fixRtlEventText} />
        <MenuItem
            label={m.reverse_rtl_start_end()}
            id="reverseRtlStartAndEndButton"
            disabled={!hasSelection || isLocked}
            disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
            onSelect={reverseRtlStartAndEnd} />
        <MenuDivider />
        <SubMenu label={m.change_case()} icon="bi-type">
            <MenuItem
                label={m.text_to_upper_case()}
                id="convertToUpperCaseButton"
                disabled={!hasSelection || isLocked}
                disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
                onSelect={convertToUpperCase} />
            <MenuItem
                label={m.text_to_lower_case()}
                id="convertToLowerCaseButton"
                disabled={!hasSelection || isLocked}
                disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
                onSelect={convertToLowerCase} />
            <MenuItem
                label={m.text_to_title_case()}
                id="convertToTitleCaseButton"
                disabled={!hasSelection || isLocked}
                disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
                onSelect={convertToTitleCase} />
            <MenuItem
                label={m.text_to_sentence_case()}
                id="convertToSentenceCaseButton"
                disabled={!hasSelection || isLocked}
                disabledReason={isLocked ? m.event_group_locked_toast() : m.no_events_selected()}
                onSelect={convertToSentenceCase} />
        </SubMenu>
    </SubMenu>
</Menu>
